import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  Link,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  VStack,
} from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { type TezosToolkit } from "@taquito/taquito";
import {
  ChainFullIds,
  type ImplicitAccount,
  type SafeSendAssetType,
  type Transaction,
  getHash,
  prettyTezAmount,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { runClaim } from "@umami/safesend";
import { useCurrentAccount, useSelectedNetwork } from "@umami/state";
import { FormProvider, useForm } from "react-hook-form";

import { useColor } from "../../../styles/useColor";
import { FormPageHeader } from "../FormPageHeader";
import { useSignPageHelpers } from "../utils";

import { ExternalLinkIcon, SafeSendIcon } from "@/assets/icons";
import { SignButton } from "@/components/SendFlow/SignButton";
import { SuccessStep } from "@/components/SendFlow/SuccessStep";

type FormValues = {
  assetType: SafeSendAssetType;
  secretHash: string;
  secret: string;
  reference: string;
};

export const SignPage = ({ operation }: { operation: Transaction }) => {
  const color = useColor();
  const sender = useCurrentAccount();
  const network = useSelectedNetwork();
  const { openWith } = useDynamicModalContext();
  const form = useForm<FormValues>({
    defaultValues: {
      assetType: operation.assetType,
      secretHash: operation.parameter.value.hash_,
      secret: "",
      reference: operation.parameter.value.comment,
    },
  });

  const { isLoading, signer } = useSignPageHelpers(
    {
      type: "implicit",
      sender: sender as ImplicitAccount,
      signer: sender as ImplicitAccount,
      operations: [
        {
          type: "claim_safe_send",
          assetType: operation.assetType,
          secretKey: "",
          contract: {
            type: "contract",
            pkh: "initialValue",
          },
          entrypoint: "claim",
          amount: "0",
          args: [],
        },
      ],
      estimates: [],
    },
    ChainFullIds.TEZOS
  );

  const onSign = async (tezosToolkit: TezosToolkit) => {
    const batchOperation = await runClaim(
      tezosToolkit,
      network,
      form.getValues("secretHash"),
      form.getValues("secret"),
      form.getValues("assetType")
    );
    await openWith(<SuccessStep hash={getHash(batchOperation)} />, { canBeOverridden: true });

    return batchOperation;
  };

  hj.stateChange("safe_send/claim_form_page");

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <FormPageHeader title="Claim SafeSend Transaction" />
          <ModalBody>
            <VStack width="100%" paddingTop="36px" paddingBottom="36px" spacing="30px">
              <VStack spacing="16px">
                <Box width="25.99px" height="24px">
                  <SafeSendIcon width="25.99px" height="24px" color={color("900")} />
                </Box>
                <VStack spacing="4px">
                  <Text
                    color={color("900")}
                    fontSize="24px"
                    fontWeight="600"
                    lineHeight="26px"
                    textAlign="center"
                  >
                    {prettyTezAmount(operation.amount)}
                  </Text>
                  <Text
                    color={color("700")}
                    fontSize="14px"
                    fontWeight="400"
                    lineHeight="22px"
                    textAlign="center"
                  >
                    Please enter Safe Send key to claim the transaction.
                  </Text>
                </VStack>
              </VStack>

              <VStack width="100%" spacing="12px">
                <Flex alignItems="center" justifyContent="space-between" width="100%">
                  <Text color={color("900")} fontSize="18px" fontWeight="600" lineHeight="24px">
                    Reference
                  </Text>
                </Flex>
                <InputGroup>
                  <Input
                    color={color("900")}
                    fontSize="18px"
                    fontWeight="400"
                    lineHeight="24px"
                    background={color("100")}
                    borderRadius="6px"
                    isReadOnly
                    value={form.getValues("reference")}
                  />
                </InputGroup>
              </VStack>

              <VStack width="100%" spacing="12px">
                <Text color={color("900")} fontSize="18px" fontWeight="600" lineHeight="24px">
                  Safe Send key
                </Text>
                <InputGroup>
                  <Input
                    isDisabled={isLoading}
                    {...form.register("secret", {
                      required: "Safe Send key is required",
                    })}
                    color={color("400")}
                    fontSize="18px"
                    fontWeight="400"
                    lineHeight="24px"
                    border={`1.5px solid ${color("300")}`}
                    borderRadius="6px"
                    placeholder="Enter Safe Send key"
                  />
                </InputGroup>
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <VStack width="100%" spacing="24px">
              <Box width="100%">
                <SignButton
                  isLoading={isLoading}
                  network={network}
                  onSubmit={onSign}
                  signer={signer}
                  text="Claim"
                />
              </Box>
              <Button
                as={Link}
                gap="10px"
                width="full"
                href={`${network.explorerUrl}/${operation.hash}`}
                isExternal
                variant="ghost"
              >
                <ExternalLinkIcon color={color("400")} />
                <Text marginRight="4px">View in TzKT</Text>
              </Button>
            </VStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
